<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PublicVolunteerSlot, PublicVolunteerRole } from '@/types/volunteer'
import VolunteerSlotPicker from '@/components/volunteer/VolunteerSlotPicker.vue'

const props = defineProps<{
  slots: PublicVolunteerSlot[]
  roles: PublicVolunteerRole[]
  selectedSlotIds: number[]
  selectedRoleIds: number[]
  fullSlotIds?: number[]
  fullRoleIds?: number[]
  errors: Record<string, string[]>
}>()

const emit = defineEmits<{
  'update:selectedSlotIds': [ids: number[]]
  'update:selectedRoleIds': [ids: number[]]
  'next-clicked': []
  'back-clicked': []
}>()

const { t } = useI18n()

const isValid = computed(
  () => props.selectedSlotIds.length > 0 && props.selectedRoleIds.length > 0,
)

function toggleRole(roleId: number): void {
  const current = props.selectedRoleIds
  if (current.includes(roleId)) {
    emit('update:selectedRoleIds', current.filter((id) => id !== roleId))
  } else {
    emit('update:selectedRoleIds', [...current, roleId])
  }
}

function isFull(role: PublicVolunteerRole): boolean {
  if (role.capacity === null) return false
  return role.confirmed_count >= role.capacity
}

function isRoleConflicted(id: number): boolean {
  return (props.fullRoleIds ?? []).includes(id)
}

/** Compute total duration in hours across selected slots */
const totalHours = computed(() => {
  const selected = props.slots.filter((s) => props.selectedSlotIds.includes(s.id))
  let minutes = 0
  for (const slot of selected) {
    const [sh, sm] = slot.start_time.split(':').map(Number)
    const [eh, em] = slot.end_time.split(':').map(Number)
    minutes += (eh * 60 + em) - (sh * 60 + (sm ?? 0))
  }
  return Math.round((minutes / 60) * 10) / 10
})
</script>

<template>
  <div class="space-y-6">
    <h2 class="text-lg font-bold text-slate-900">{{ t('volunteer.wizard.step2.title') }}</h2>
    <p class="text-sm text-slate-600">{{ t('volunteer.wizard.step2.intro') }}</p>

    <!-- Roles -->
    <section>
      <h3 class="text-sm font-semibold text-slate-700 mb-2">{{ t('volunteer.wizard.step2.role_heading') }}</h3>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="role in roles"
          :key="role.id"
          type="button"
          :aria-pressed="selectedRoleIds.includes(role.id)"
          :aria-disabled="isFull(role)"
          :disabled="isFull(role)"
          class="flex flex-col items-start rounded-lg border px-4 py-2.5 text-left text-sm transition-colors max-w-xs"
          :class="{
            'bg-sky-600 border-sky-600 text-white': selectedRoleIds.includes(role.id) && !isRoleConflicted(role.id),
            'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed': isFull(role),
            'border-amber-400 ring-2 ring-amber-200 bg-amber-50 text-amber-800': isRoleConflicted(role.id),
            'bg-white border-slate-200 text-slate-700 hover:border-sky-400 hover:bg-sky-50': !selectedRoleIds.includes(role.id) && !isFull(role) && !isRoleConflicted(role.id),
          }"
          @click="toggleRole(role.id)"
        >
          <span class="font-medium">{{ role.name }}</span>
          <span v-if="role.description" class="text-xs opacity-75 mt-0.5">{{ role.description }}</span>
          <span v-if="isFull(role)" class="text-xs mt-1 opacity-75">{{ t('volunteer.wizard.step2.slot_full') }}</span>
        </button>
      </div>
      <p v-if="errors['role_ids']" class="mt-1 text-xs text-red-600">{{ errors['role_ids'][0] }}</p>
      <p v-else-if="selectedRoleIds.length === 0" class="mt-1 text-xs text-slate-400">
        {{ t('volunteer.wizard.errors.no_role') }}
      </p>
    </section>

    <!-- Slots -->
    <section>
      <h3 class="text-sm font-semibold text-slate-700 mb-2">{{ t('volunteer.wizard.step2.slot_heading') }}</h3>
      <VolunteerSlotPicker
        :slots="slots"
        :model-value="selectedSlotIds"
        :full-slot-ids="fullSlotIds"
        @update:model-value="emit('update:selectedSlotIds', $event)"
      />
      <p v-if="errors['slot_ids']" class="mt-1 text-xs text-red-600">{{ errors['slot_ids'][0] }}</p>
      <p v-else-if="selectedSlotIds.length === 0" class="mt-1 text-xs text-slate-400">
        {{ t('volunteer.wizard.errors.no_slot') }}
      </p>
    </section>

    <!-- Commitment summary -->
    <p
      v-if="selectedSlotIds.length > 0 || selectedRoleIds.length > 0"
      class="text-sm text-sky-700 font-medium"
    >
      {{
        t('volunteer.wizard.step2.commitment_summary', {
          slotCount: selectedSlotIds.length,
          hours: totalHours,
          roleCount: selectedRoleIds.length,
        })
      }}
    </p>

    <!-- Navigation -->
    <div class="flex gap-3 pt-2">
      <button
        type="button"
        class="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
        @click="emit('back-clicked')"
      >
        {{ t('common.back') }}
      </button>
      <button
        type="button"
        :disabled="!isValid"
        class="flex-1 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        @click="emit('next-clicked')"
      >
        {{ t('common.next') }}
      </button>
    </div>
  </div>
</template>
